import React from 'react';
import { s3StaticBucketURL, s3DynamicBucketURL, getCdnImageUrl } from '@utils/cloudflare';
import Image from 'next/legacy/image';
import Head from 'next/head';

/**
 * CloudImage Component
 *
 * A Next.js 12 compatible image component that provides optimized image delivery through
 * Cloudflare CDN while maintaining essential Next.js Image features like lazy loading,
 * automatic srcSet generation, and layout control.
 *
 * Features:
 * - Cloudflare CDN optimization for improved performance
 * - Support for both static and dynamic S3 buckets
 * - Optional image preloading for critical images
 * - Configurable image quality
 * - Automatic lazy loading and responsive images
 * - Full compatibility with Next.js 12 Image API
 *
 * Note: This component uses a custom loader to disable Next.js's built-in optimization
 * in favor of Cloudflare's CDN optimization, which is required for external URLs in Next.js 12.
 *
 * @example
 * ```tsx
 * // Basic usage with static bucket (default)
 * <CloudImage
 *   src="/hero-image.jpg"
 *   width={800}
 *   height={600}
 *   alt="Hero image"
 * />
 *
 * // With dynamic bucket and custom quality
 * <CloudImage
 *   src="/user-upload.jpg"
 *   width={400}
 *   height={300}
 *   alt="User uploaded image"
 *   bucket="dynamic"
 *   quality={85}
 * />
 *
 * // With preloading for critical images
 * <CloudImage
 *   src="/above-fold-image.jpg"
 *   width={1200}
 *   height={800}
 *   alt="Above the fold image"
 *   preload={true}
 *   priority
 * />
 *
 * // With additional Next.js Image props
 * <CloudImage
 *   src="/responsive-image.jpg"
 *   width={600}
 *   height={400}
 *   alt="Responsive image"
 *   className="rounded-lg"
 *   placeholder="blur"
 *   blurDataURL="data:image/jpeg;base64,..."
 * />
 * ```
 *
 * @param props - CloudImage component props
 * @param props.src - Image source path (relative to bucket)
 * @param props.width - Image width in pixels
 * @param props.height - Image height in pixels
 * @param props.alt - Alternative text for accessibility
 * @param props.quality - Image quality (0-100), defaults to 95
 * @param props.preload - Whether to preload the image in document head, defaults to false
 * @param props.bucket - S3 bucket type ('static' | 'dynamic'), defaults to 'static'
 * @param props.[...rest] - Any additional props are passed through to Next.js Image component
 *
 * @returns React component that renders an optimized image through Cloudflare CDN
 */
type CloudImageProps = {
  src: string;
  width: number;
  height: number;
  alt: string;
  quality?: number;
  preload?: boolean;
  bucket?: 'static' | 'dynamic';
  [x: string]: any;
};

// Next.js 12 doesn’t support unoptimized for external URLs so we're using a custom loader
// This disables all of Next.js’s optimization, but still gives:
// - Lazy loading
// - Automatic srcSet
// - Layout modes (fill, intrinsic, etc.)
// - Styling control
// vs <img>
const cloudflareLoader = ({ src }: { src: string }) => src;

export default function CloudImage({
  src,
  width,
  height,
  alt,
  quality = 95,
  preload = false,
  bucket = 'static',
  ...props
}: CloudImageProps) {
  const urlPrefix = bucket === 'static' ? s3StaticBucketURL : s3DynamicBucketURL;
  const imageUrl = getCdnImageUrl(width, src, urlPrefix, quality);

  return (
    <>
      {preload && (
        <Head>
          <link rel="preload" as="image" href={imageUrl} />
        </Head>
      )}

      <Image
        src={imageUrl}
        width={width}
        height={height}
        alt={alt}
        loader={cloudflareLoader}
        {...props}
      />
    </>
  );
}
